import { useRef, useState } from "react";
import { itemById } from "@zend/content";
import { api } from "../api/http.js";
import { requestScope } from "../api/scope.js";
import { useSession } from "../state/session.js";
import { useBalances, type Balances } from "../state/balances.js";
import { errorText, useBase } from "../base/state.js";
import { Button } from "../ui/Button.js";
import { useT, getLocale } from "../i18n/index.js";
import type { InventoryStack } from "./stock.js";
import { toast } from "../ui/Toast.js";

interface UseResult { collectionRef: string; inventory: InventoryStack[]; balances: Balances; resources: Record<string, number>; stamina: number; reused: boolean }

export function UseConsumable({ itemId, owned }: { itemId: string; owned: number }) {
  const t = useT(), definition = itemById(itemId);
  const [quantity, setQuantity] = useState(1), [busy, setBusy] = useState(false), [error, setError] = useState(""), [result, setResult] = useState("");
  const requests = useRef(new Map<number, string>()), inFlight = useRef(false);
  const amount = Number(definition.params["amount"]) * quantity;
  const resource = definition.kind === "stamina" ? t("res.stamina") : t(`res.${String(definition.params["resource"])}`);
  const consume = async () => {
    if (inFlight.current || quantity > owned) return;
    const { generation } = requestScope();
    const requestId = requests.current.get(quantity) ?? crypto.randomUUID(); requests.current.set(quantity, requestId);
    inFlight.current = true; setBusy(true); setError(""); setResult("");
    try {
      const data = await api<UseResult>("POST", "/api/inventory/use", { itemId, qty: quantity, requestId });
      if (requestScope().generation !== generation) return;
      requests.current.delete(quantity);
      useSession.setState({ inventory: data.inventory }); useBalances.getState().setBalances(data.balances, data.collectionRef);
      setResult(t("inventory.used", { n: amount.toLocaleString(getLocale()), resource }));
      toast.success(t("inventory.used", { n: amount.toLocaleString(getLocale()), resource }));
      void useBase.getState().refresh();
    } catch (e) { if (requestScope().generation === generation) setError(errorText(e)); }
    finally { inFlight.current = false; if (requestScope().generation === generation) setBusy(false); }
  };
  return <section className="inventory-consume" data-testid="inventory-consume">
    <label>{t("inventory.useQuantity")}<input type="number" min={1} max={Math.min(100, owned)} step={1} value={quantity} disabled={busy} onChange={event => { setQuantity(Math.max(1, Math.min(100, owned, Math.floor(Number(event.target.value) || 1)))); setResult(""); setError(""); }} /></label>
    <p>{t("inventory.usePreview", { qty: quantity, n: amount.toLocaleString(getLocale()), resource })}</p><p className="hint">{t("inventory.noOverflowLoss")}</p>
    {error ? <p role="alert">{error}</p> : null}{result ? <p role="status">{result}</p> : null}
    <Button variant="primary" loading={busy} disabled={owned < quantity} onClick={() => void consume()} data-testid="inventory-use-confirm">{t(definition.kind === "stamina" ? "inventory.refill" : "inventory.openCrates", { n: quantity })}</Button>
  </section>;
}
